import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useGuild } from '../contexts/GuildContext';
import { Button } from '../components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Skeleton } from '../components/ui/skeleton';
import {
    Ticket,
    LayoutDashboard,
    Users,
    Settings,
    FileText,
    Moon,
    Sun,
    LogOut,
    Menu,
    X,
    ChevronRight,
    Server,
    Plus,
    ExternalLink
} from 'lucide-react';

const sidebarLinks = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Overview', exact: true },
    { path: '/dashboard/tickets', icon: Ticket, label: 'Tickets' },
    { path: '/dashboard/staff', icon: Users, label: 'Staff' },
    { path: '/dashboard/transcripts', icon: FileText, label: 'Transcripts' },
    { path: '/dashboard/settings', icon: Settings, label: 'Settings' },
];

export default function Dashboard() {
    const { user, logout, loading: authLoading, token } = useAuth();
    const { theme, toggleTheme } = useTheme();
    const { guilds, selectedGuild, guildData, loading: guildLoading, selectGuild } = useGuild();
    const navigate = useNavigate();
    const location = useLocation();
    const [sidebarOpen, setSidebarOpen] = useState(false);

    useEffect(() => {
        // Only redirect if loading is complete AND there's no user AND no token
        if (!authLoading && !user && !token) {
            navigate('/');
        }
    }, [user, authLoading, token, navigate]);

    const isActive = (path, exact = false) => {
        if (exact) {
            return location.pathname === path;
        }
        return location.pathname.startsWith(path);
    };

    const getAvatarUrl = (userId, avatarHash) => {
        if (!avatarHash) return null;
        return `https://cdn.discordapp.com/avatars/${userId}/${avatarHash}.png`;
    };

    const getGuildIcon = (guildId, iconHash) => {
        if (!iconHash) return null;
        return `https://cdn.discordapp.com/icons/${guildId}/${iconHash}.png`;
    };

    if (authLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="text-center">
                    <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center mx-auto mb-4 animate-pulse">
                        <Ticket className="w-6 h-6 text-primary-foreground" />
                    </div>
                    <p className="text-muted-foreground">Loading...</p>
                </div>
            </div>
        );
    }

    const botGuilds = guilds.filter(g => g.bot_present);
    const availableGuilds = guilds.filter(g => !g.bot_present);

    return (
        <div className="min-h-screen bg-background flex">
            {/* Mobile Sidebar Overlay */}
            {sidebarOpen && (
                <div 
                    className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            {/* Sidebar */}
            <aside className={`fixed lg:sticky top-0 left-0 z-50 w-64 h-screen bg-card border-r flex flex-col transition-transform duration-300 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
                {/* Logo */}
                <div className="h-16 border-b flex items-center justify-between px-4">
                    <div className="flex items-center gap-2">
                        <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
                            <Ticket className="w-5 h-5 text-primary-foreground" />
                        </div>
                        <span className="text-lg font-bold">TicketBot</span>
                    </div>
                    <button 
                        className="lg:hidden p-2 hover:bg-muted rounded-lg"
                        onClick={() => setSidebarOpen(false)}
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Guild Selector */}
                <div className="p-4 border-b">
                    <label className="text-xs text-muted-foreground mb-2 block">Select Server</label>
                    {guildLoading ? (
                        <Skeleton className="h-10 w-full" />
                    ) : botGuilds.length > 0 ? (
                        <Select value={selectedGuild || ''} onValueChange={selectGuild}>
                            <SelectTrigger data-testid="guild-selector">
                                <SelectValue placeholder="Select a server" />
                            </SelectTrigger>
                            <SelectContent>
                                {botGuilds.map(guild => (
                                    <SelectItem key={guild.id} value={guild.id}>
                                        <div className="flex items-center gap-2">
                                            {guild.icon ? (
                                                <img 
                                                    src={getGuildIcon(guild.id, guild.icon)} 
                                                    alt="" 
                                                    className="w-5 h-5 rounded-full"
                                                />
                                            ) : (
                                                <Server className="w-5 h-5" />
                                            )}
                                            <span className="truncate">{guild.name}</span>
                                        </div>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    ) : (
                        <div className="text-sm text-muted-foreground p-2 text-center rounded-lg border border-dashed">
                            No servers with bot
                        </div>
                    )}
                </div>

                {/* Navigation */}
                <ScrollArea className="flex-1 py-4">
                    <nav className="px-3 space-y-1">
                        {sidebarLinks.map(link => (
                            <button
                                key={link.path}
                                onClick={() => {
                                    navigate(link.path);
                                    setSidebarOpen(false);
                                }}
                                disabled={!selectedGuild}
                                className={`sidebar-link w-full ${isActive(link.path, link.exact) ? 'active' : ''} ${!selectedGuild ? 'opacity-50 cursor-not-allowed' : ''}`}
                                data-testid={`nav-${link.label.toLowerCase()}`}
                            >
                                <link.icon className="w-5 h-5" />
                                <span>{link.label}</span>
                                {isActive(link.path, link.exact) && (
                                    <ChevronRight className="w-4 h-4 ml-auto" />
                                )}
                            </button>
                        ))}
                    </nav>

                    {/* Available Servers */}
                    {availableGuilds.length > 0 && (
                        <div className="px-4 mt-6">
                            <p className="text-xs text-muted-foreground mb-2">Add Bot to Server</p>
                            <div className="space-y-2">
                                {availableGuilds.slice(0, 5).map(guild => (
                                    <div 
                                        key={guild.id}
                                        className="flex items-center justify-between p-2 rounded-lg bg-muted/50 text-sm"
                                    >
                                        <div className="flex items-center gap-2 truncate">
                                            {guild.icon ? (
                                                <img 
                                                    src={getGuildIcon(guild.id, guild.icon)} 
                                                    alt="" 
                                                    className="w-5 h-5 rounded-full"
                                                />
                                            ) : (
                                                <Server className="w-5 h-5" />
                                            )}
                                            <span className="truncate">{guild.name}</span>
                                        </div>
                                        <Button 
                                            size="sm" 
                                            variant="ghost" 
                                            className="h-7 w-7 p-0"
                                            onClick={() => window.open(`https://discord.com/api/oauth2/authorize?client_id=1477751030426112261&permissions=8&scope=bot%20applications.commands&guild_id=${guild.id}`, '_blank')}
                                            data-testid={`add-bot-${guild.id}`}
                                        >
                                            <Plus className="w-4 h-4" />
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </ScrollArea>

                {/* User */}
                <div className="p-4 border-t">
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <button className="flex items-center gap-3 w-full p-2 rounded-lg hover:bg-muted transition-colors" data-testid="user-menu">
                                <Avatar className="w-8 h-8">
                                    <AvatarImage src={getAvatarUrl(user?.discord_id, user?.avatar)} />
                                    <AvatarFallback>{user?.username?.charAt(0).toUpperCase()}</AvatarFallback>
                                </Avatar>
                                <div className="flex-1 text-left">
                                    <p className="text-sm font-medium truncate">{user?.username}</p>
                                </div>
                            </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-56">
                            <DropdownMenuLabel>My Account</DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={toggleTheme}>
                                {theme === 'dark' ? <Sun className="w-4 h-4 mr-2" /> : <Moon className="w-4 h-4 mr-2" />}
                                {theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={logout} className="text-destructive" data-testid="logout-btn">
                                <LogOut className="w-4 h-4 mr-2" />
                                Logout
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </aside>

            {/* Main Content */}
            <div className="flex-1 flex flex-col min-h-screen">
                {/* Top Bar */}
                <header className="h-16 border-b bg-card/50 backdrop-blur-xl sticky top-0 z-30 flex items-center px-4 gap-4">
                    <button 
                        className="lg:hidden p-2 hover:bg-muted rounded-lg"
                        onClick={() => setSidebarOpen(true)}
                        data-testid="mobile-menu-btn"
                    >
                        <Menu className="w-5 h-5" />
                    </button>

                    <div className="flex-1">
                        {guildData && (
                            <div className="flex items-center gap-2">
                                {guildData.icon && (
                                    <img 
                                        src={getGuildIcon(guildData.guild_id, guildData.icon)} 
                                        alt="" 
                                        className="w-6 h-6 rounded-full"
                                    />
                                )}
                                <span className="font-semibold">{guildData.name}</span>
                            </div>
                        )}
                    </div>

                    <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="topbar-theme-toggle">
                        {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                    </Button>
                </header>

                {/* Page Content */}
                <main className="flex-1 p-4 md:p-6">
                    {selectedGuild ? (
                        <motion.div
                            key={location.pathname}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.2 }}
                        >
                            <Outlet />
                        </motion.div>
                    ) : (
                        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
                            <Server className="w-16 h-16 text-muted-foreground mb-4" />
                            <h2 className="text-2xl font-bold mb-2">No Server Selected</h2>
                            <p className="text-muted-foreground max-w-md mb-6">
                                {botGuilds.length > 0 
                                    ? 'Select a server from the sidebar to view and manage tickets.'
                                    : 'Add the bot to a server where you have admin permissions to get started.'}
                            </p>
                            {botGuilds.length === 0 && (
                                <Button 
                                    className="discord-btn flex items-center gap-2"
                                    onClick={() => window.open('https://discord.com/api/oauth2/authorize?client_id=1477751030426112261&permissions=8&scope=bot%20applications.commands', '_blank')}
                                    data-testid="add-bot-cta"
                                >
                                    <ExternalLink className="w-4 h-4" />
                                    Add Bot to Server
                                </Button>
                            )}
                        </div>
                    )}
                </main>
            </div>
        </div>
    );
}
